/**
 * Serialization utilities for appointment notes.
 *
 * New notes are stored as JSON (BlockNote document blocks).
 * Legacy plain-text notes are detected and parsed as markdown on load.
 * This avoids the lossy markdown round-trip that corrupts checklists.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEditor = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyBlock = any;

/**
 * Detects whether a stored note value is JSON (BlockNote document)
 * or plain text / markdown (legacy).
 */
function isJsonDocument(value: string): boolean {
  const trimmed = value.trimStart();
  return trimmed.startsWith('[');
}

/**
 * Parse a stored note string into BlockNote blocks.
 * - If JSON: deserialize directly (lossless round-trip)
 * - If plain text/markdown: use editor's markdown parser
 */
export async function parseNoteToBlocks(
  editor: AnyEditor,
  value: string,
): Promise<AnyBlock[]> {
  if (!value) return [];

  if (isJsonDocument(value)) {
    try {
      return JSON.parse(value) as AnyBlock[];
    } catch {
      // Malformed JSON — fall through to markdown parsing
    }
  }

  // Legacy plain text or markdown
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  return await editor.tryParseMarkdownToBlocks(value);
}

/**
 * Serialize BlockNote document blocks to a string for storage.
 * Uses JSON for lossless round-trip (checklists, formatting preserved).
 */
export function serializeBlocks(blocks: AnyBlock[]): string {
  return JSON.stringify(blocks);
}

/**
 * Checks whether an inline content array contains only empty text.
 * BlockNote may serialize an empty paragraph's content as
 * `[{"type":"text","text":"","styles":{}}]` — a non-empty array
 * with a single text node whose text is blank.
 */
function isInlineContentEmpty(content: AnyBlock[]): boolean {
  return content.every(
    (ic: AnyBlock) => ic.type === 'text' && (!ic.text || !ic.text.trim()),
  );
}

/**
 * Detects whether a note string is "structurally empty" — i.e. it contains
 * no meaningful user content, even though BlockNote may serialize it as
 * `[{"type":"paragraph","props":{...},"content":[{"type":"text","text":"","styles":{}}],"children":[]}]`.
 */
export function isEmptyNoteContent(value: string | null | undefined): boolean {
  if (!value) return true;
  if (!isJsonDocument(value)) return !value.trim();
  try {
    const blocks = JSON.parse(value) as AnyBlock[];
    return blocks.every(
      (b: AnyBlock) =>
        b.type === 'paragraph' &&
        (!b.content ||
          b.content === '' ||
          (Array.isArray(b.content) && isInlineContentEmpty(b.content))) &&
        (!b.children || b.children.length === 0),
    );
  } catch {
    return false;
  }
}
